import { useCallback, useEffect, useRef, useState } from 'react';

import { useContentNavigationHash } from './useContentNavigationHash';
import { ScrollEndFallbackEvent } from './useScrollEndFallback';
import { useSectionObserver } from './useSectionObserver';

type UseContentNavigationStateOptions = {
  currentSection?: string;
  defaultSection?: string;
  onSectionChange?: (id: string) => void;
};

type ContentNavigationState = {
  currentSection: string | undefined;
  goToSection: (id: string) => void;
  registerSectionElement: (id: string, element: HTMLElement) => () => void;
};

export const useContentNavigationState = ({
  currentSection,
  defaultSection,
  onSectionChange,
}: UseContentNavigationStateOptions): ContentNavigationState => {
  const { sectionFromHash, synchronizeSectionHash } =
    useContentNavigationHash();
  const [activeSection, setActiveSection] = useState(
    () => sectionFromHash ?? defaultSection,
  );
  const activeSectionRef = useRef(activeSection);
  const requestedSectionRef = useRef(sectionFromHash);

  const updateActiveSection = useCallback(
    (id: string): void => {
      if (activeSectionRef.current === id) return;

      activeSectionRef.current = id;
      setActiveSection(id);
      onSectionChange?.(id);
    },
    [onSectionChange],
  );

  const updateObservedSection = useCallback(
    (id: string): void => {
      updateActiveSection(id);
      synchronizeSectionHash(id);
    },
    [synchronizeSectionHash, updateActiveSection],
  );

  const handleSectionInView = useCallback(
    (id: string): void => {
      const requestedSection = requestedSectionRef.current;
      if (requestedSection && requestedSection !== id) return;

      requestedSectionRef.current = undefined;
      updateObservedSection(id);
    },
    [updateObservedSection],
  );

  const handleScrollEndFallback = useCallback(
    ({ id, type }: ScrollEndFallbackEvent): void => {
      if (type === 'left-end') {
        requestedSectionRef.current = undefined;
        updateObservedSection(id);
        return;
      }

      if (requestedSectionRef.current) {
        requestedSectionRef.current = undefined;
        return;
      }

      updateObservedSection(id);
    },
    [updateObservedSection],
  );

  const { getSectionElement, observeSectionElement } = useSectionObserver({
    onSectionInView: handleSectionInView,
    onScrollEndFallback: handleScrollEndFallback,
  });

  const registerSectionElement = useCallback(
    (id: string, element: HTMLElement): (() => void) => {
      observeSectionElement(id, element);
      if (requestedSectionRef.current === id) {
        element.scrollIntoView?.();
      }
      return () => {
        if (requestedSectionRef.current === id) {
          requestedSectionRef.current = undefined;
        }
        observeSectionElement(id, null);
      };
    },
    [observeSectionElement],
  );

  const goToSection = useCallback(
    (id: string): void => {
      requestedSectionRef.current = id;
      updateActiveSection(id);
      getSectionElement(id)?.scrollIntoView?.();
    },
    [getSectionElement, updateActiveSection],
  );

  useEffect(() => {
    if (sectionFromHash) {
      if (activeSectionRef.current === sectionFromHash) return;

      const isAlreadyRequested =
        requestedSectionRef.current === sectionFromHash;
      requestedSectionRef.current = sectionFromHash;
      if (!isAlreadyRequested) {
        getSectionElement(sectionFromHash)?.scrollIntoView?.();
      }
      updateActiveSection(sectionFromHash);
      return;
    }

    requestedSectionRef.current = undefined;
    if (activeSectionRef.current === undefined && defaultSection) {
      updateActiveSection(defaultSection);
    }
  }, [defaultSection, getSectionElement, sectionFromHash, updateActiveSection]);

  return {
    currentSection: currentSection ?? activeSection,
    goToSection,
    registerSectionElement,
  };
};
